<template>
  <v-card class="session-status-card" elevation="2">
    <v-card-title class="d-flex align-center">
      <v-icon :color="statusConfig.color" class="mr-3">
        {{ statusConfig.icon }}
      </v-icon>
      <span>Estado de la Sesión</span>
      <v-spacer />
      <v-chip
        :color="statusConfig.color"
        size="small"
        variant="tonal"
      >
        {{ statusConfig.text }}
      </v-chip>
    </v-card-title>

    <v-card-text>
      <!-- PIN Code Display -->
      <div v-if="pinCode" class="mb-4">
        <div class="text-subtitle-2 text-medium-emphasis mb-1">Código PIN</div>
        <v-chip
          color="primary"
          size="large"
          class="font-monospace"
          style="letter-spacing: 2px"
        >
          {{ pinCode }}
        </v-chip>
      </div>

      <!-- Session Details -->
      <div class="d-flex flex-wrap ga-4 mb-4">
        <div>
          <div class="text-subtitle-2 text-medium-emphasis">Participantes</div>
          <div class="text-h6 d-flex align-center">
            <v-icon left size="small" color="success">mdi-account-multiple</v-icon>
            {{ waitingCount }}
            <span class="text-caption text-medium-emphasis ml-1">esperando</span>
          </div>
        </div>

        <div v-if="sessionId">
          <div class="text-subtitle-2 text-medium-emphasis">ID de Sesión</div>
          <div class="text-body-2 font-monospace">{{ sessionId }}</div>
        </div>
      </div>

      <!-- Status Message -->
      <div class="mb-3">
        <v-alert
          :type="statusConfig.alertType"
          variant="tonal"
          density="compact"
          class="text-body-2"
        >
          <v-icon left size="small">{{ statusConfig.icon }}</v-icon>
          {{ statusConfig.description }}
        </v-alert>
      </div>

      <!-- Connection Status -->
      <div class="d-flex align-center ga-2">
        <v-icon
          :color="connectionConfig.color"
          size="small"
        >
          {{ connectionConfig.icon }}
        </v-icon>
        <span class="text-caption">{{ connectionConfig.text }}</span>
      </div>
    </v-card-text>

    <!-- Progress indicator for waiting state -->
    <v-card-text v-if="sessionStatus === 'waiting' && waitingCount > 0" class="pt-0">
      <v-progress-linear
        :model-value="Math.min(waitingCount * 15, 100)"
        :color="statusConfig.color"
        height="6"
        rounded
      />
      <div class="text-caption text-center mt-2 text-medium-emphasis">
        Preparando sesión...
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

// Computed properties
const sessionStatus = computed(() => store.getters['session/getSessionStatus'])
const participantStatus = computed(() => store.getters['session/getParticipantStatus'])
const sessionId = computed(() => store.getters['session/getSessionId'])
const pinCode = computed(() => store.getters['session/getPinCode'])
const waitingCount = computed(() => store.getters['session/getWaitingCount'])
const isWebsocketConnected = computed(() => store.getters['session/isWebsocketConnected'])

const statusConfig = computed(() => {
  switch (sessionStatus.value) {
    case null:
    case undefined:
    case '':
      return {
        icon: 'mdi-plus-circle-outline',
        color: 'info',
        text: 'No Iniciada',
        alertType: 'info',
        description: 'No se ha creado una sesión aún. Los estudiantes pueden unirse cuando ingresen su PIN.'
      }
    case 'waiting':
      return {
        icon: 'mdi-clock-time-four-outline',
        color: 'orange',
        text: 'En Espera',
        alertType: 'warning',
        description: 'Los estudiantes están esperando en la sala. Inicie cuando esté listo.'
      }
    case 'active':
      return {
        icon: 'mdi-play-circle',
        color: 'success',
        text: 'Activo',
        alertType: 'success',
        description: 'El quiz está activo y los estudiantes pueden responder.'
      }
    case 'completed':
      return {
        icon: 'mdi-check-circle',
        color: 'success',
        text: 'Completado',
        alertType: 'info',
        description: 'La sesión ha sido completada exitosamente.'
      }
    default:
      console.warn('Unexpected session status:', sessionStatus.value)
      return {
        icon: 'mdi-information-outline',
        color: 'grey',
        text: 'Preparando',
        alertType: 'info',
        description: `Preparando sesión... (Estado: ${sessionStatus.value || 'indefinido'})`
      }
  }
})

const connectionConfig = computed(() => {
  if (isWebsocketConnected.value) {
    return {
      icon: 'mdi-wifi',
      color: 'success',
      text: 'Conectado en tiempo real'
    }
  } else {
    return {
      icon: 'mdi-wifi-strength-1',
      color: 'warning',
      text: 'Actualizando periódicamente'
    }
  }
})
</script>

<style scoped>
.session-status-card {
  border-left: 4px solid;
  border-left-color: var(--v-theme-primary);
}

.font-monospace {
  font-family: 'Courier New', monospace;
}

.v-progress-linear {
  border-radius: 8px;
}
</style>